import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ISrsBallotDetail } from '../../shared/models/sr/srs-ballot-details';
import { SrsBallotReport } from '../../shared/models/reports/srs-ballot-report';
import { IBallotReportVoteResultsBreakdownPercentage } from '../../shared/models/reports/ballot-report';
import { SrsBallotVoteTypes, SrsBallotResult, SrsBallotVoteType } from '../../shared/models/sr/srs-ballot-vote-type';
import { ISrsBallotVote } from '../../shared/models/sr/srs-vote';
import { appConstants } from '../../../../shared/constants/app-constants';
import { Country, Location } from '../../../../shared/constants/srs-ballot.constants';
import { arrayUtility } from '../../../../shared/utilities/array-utility';

@Component({
    selector: 'app-srs-report',
    templateUrl: './srs-report.component.html',
    styleUrl: './srs-report.component.scss',
    standalone: false
})
export class SrsReportComponent implements OnChanges  {

  @Input() srsBallotDetail:  ISrsBallotDetail | null = null;
  @Input() isSrsBallot: boolean = false;

  ballotReport: SrsBallotReport | undefined = undefined;

  constructor(
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['srsBallotDetail'] && changes['srsBallotDetail'].currentValue && this.srsBallotDetail) {
      this.ballotReport = new SrsBallotReport();
      this.ballotReport = new SrsBallotReport();
      this.ballotReport.documentHeader = `Systematic Review Ballot - ${this.srsBallotDetail.designation}`;
      this.ballotReport.documentFilename = `${this.srsBallotDetail.designation} Systematic Review Ballot Report.pdf`;
      this.ballotReport.designation = this.srsBallotDetail.designation;
      this.ballotReport.title = this.srsBallotDetail.designationTitle;
      this.ballotReport.publishedDate = this.srsBallotDetail.publicationCreateDate;
      this.ballotReport.committee = this.srsBallotDetail.committee;
      this.ballotReport.committeeTitle = this.srsBallotDetail.committeeTitle;
      this.ballotReport.projectManager = this.srsBallotDetail.projectManager;
      this.ballotReport.associatedDocuments = this.srsBallotDetail.associatedDocuments.map(x => x.datedDesignation);
      this.ballotReport.startDate = this.srsBallotDetail.ballotStartDate;
      this.ballotReport.endDate = this.srsBallotDetail.ballotEndDate;
      this.ballotReport.status = this.srsBallotDetail.status;
      this.ballotReport.notes = this.srsBallotDetail.notes;

      this.setBallotResultBreakdown();
      this.setCommitteeVotes();
      this.setQuestions();
    }
  }

  mapSrsBallotResultToBallotReportVoteResultsBreakdownPercentages(result: SrsBallotResult): IBallotReportVoteResultsBreakdownPercentage[] {
    return [
      {
        label: SrsBallotVoteTypes.SVT_RECONFIRM.name,
        fillColor: SrsBallotVoteTypes.SVT_RECONFIRM.color,
        value: result.SVT_RECONFIRM.value,
        percentage: result.SVT_RECONFIRM.percentage
      },
      {
        label: SrsBallotVoteTypes.SVT_REVISE.name,
        fillColor: SrsBallotVoteTypes.SVT_REVISE.color,
        value: result.SVT_REVISE.value,
        percentage: result.SVT_REVISE.percentage
      },
      {
        label: SrsBallotVoteTypes.SVT_WITHDRAW.name,
        fillColor: SrsBallotVoteTypes.SVT_WITHDRAW.color,
        value: result.SVT_WITHDRAW.value,
        percentage: result.SVT_WITHDRAW.percentage
      },
      {
        label: SrsBallotVoteTypes.SVT_NONE.name,
        fillColor: SrsBallotVoteTypes.SVT_NONE.color,
        value: result.SVT_NONE.value,
        percentage: result.SVT_NONE.percentage
      }
    ];
  }

  setBallotResultBreakdown(): void {
    if(this.srsBallotDetail && this.ballotReport){
      let isJoint = this.srsBallotDetail.isJoint;
      let eligibleVoters = this.srsBallotDetail.votes.filter((vote: ISrsBallotVote) => vote.isEligible);
      let overall = this.getBallotResult(eligibleVoters);
      this.ballotReport.voteResultBreakdown = [
        {
          country: 'Overall',
          totalNumberOfVotes: eligibleVoters.length,
          votePercentages: this.mapSrsBallotResultToBallotReportVoteResultsBreakdownPercentages(overall)
        }
      ];

      if (isJoint) {
        var ausVoters = eligibleVoters.filter(vote => vote.country && Country.Australia.includes(vote.country.toLowerCase()));
        var nzVoters = eligibleVoters.filter(vote => vote.country && Country.NewZealand.includes(vote.country.toLowerCase()));

        let ausResult = this.getBallotResult(ausVoters);
        let nzResult = this.getBallotResult(nzVoters);

        this.ballotReport.voteResultBreakdown.push({
          country: Location.AUSTRALIA,
          totalNumberOfVotes: ausVoters.length,
          votePercentages: this.mapSrsBallotResultToBallotReportVoteResultsBreakdownPercentages(ausResult)
        });

        this.ballotReport.voteResultBreakdown.push({
          country: Location.NEWZEALAND,
          totalNumberOfVotes: nzVoters.length,
          votePercentages: this.mapSrsBallotResultToBallotReportVoteResultsBreakdownPercentages(nzResult)
        });
      }
    }
  }

  setCommitteeVotes(): void {
    if (this.srsBallotDetail && this.ballotReport) {
      this.ballotReport.committeeVotes = this.srsBallotDetail.votes.map((vote) => {
        const voteType = SrsBallotVoteTypes[vote.voteTypeId as keyof typeof SrsBallotVoteTypes];

        return {
          committeeMember: vote.userName,
          organisation: vote.organisation,
          role: vote.role,
          country: vote.country,
          votingRights: this.getVoterEligibility(this.srsBallotDetail, vote),
          answers: [
            voteType.name,
            vote.voteTypeId !== SrsBallotVoteTypes.SVT_REVISE.key
              ? 'NA'
              : vote.comments[0].leadProjectProposal ? 'Yes' : 'No',
          ],
        };
      });
    }
  }


  setQuestions(): void {
    if(this.srsBallotDetail && this.ballotReport) {
      let isJoint = this.srsBallotDetail.isJoint;
      this.ballotReport.questions = [
        {
          questionNumber: 'Q1',
          question: appConstants.BallotVoteQuestions.ACTION_TO_TAKE_WITH_THIS_STANDARD,
          answerOptions: [
            SrsBallotVoteTypes.SVT_RECONFIRM.name,
            SrsBallotVoteTypes.SVT_REVISE.name,
            SrsBallotVoteTypes.SVT_WITHDRAW.name,
            SrsBallotVoteTypes.SVT_NONE.name
          ],
          comments: this.srsBallotDetail.votes
            .filter((vote: ISrsBallotVote) => vote.isEligible)
            .map(x => ({
              commenter: x.userName,
              comment: x.comments[0]?.comment,
            })) ?? undefined
        },
        {
          questionNumber: 'Q2',
          question: appConstants.BallotVoteQuestions.LEAD_PROJECT_PROPOSAL_DEVELOPMENT,
          answerOptions: ['Yes', 'No']
        },
      ];
    }
  }

  public getAggregatedVotesByEligibleVoters(eligibleVoters: ISrsBallotVote[]): SrsBallotVoteType[] {
    // Group the records by vote type
    const groupedByVoteType = arrayUtility.groupBy(eligibleVoters, 'voteTypeId');

    // Calculate the total votes
    const totalVotes = arrayUtility.getTotal(groupedByVoteType, 'value');

    // Map the vote types and merge the votes value and percentage
    return Object.values(SrsBallotVoteTypes).map((voteType: SrsBallotVoteType) => {
        const foundVote = groupedByVoteType.find(v => v.key === voteType.key) || { value: 0 };

        return {
            ...voteType,
            value: foundVote.value,
            percentage: totalVotes > 0 ? Math.round(foundVote.value / totalVotes) : 0
        };
    });
}


  public getBallotResult(eligibleVoters: ISrsBallotVote[]): SrsBallotResult {
    const groupedByVoteType = this.getAggregatedVotesByEligibleVoters(eligibleVoters);

    const ballotResult: SrsBallotResult = {
      SVT_NONE: groupedByVoteType.find(v => v.key === SrsBallotVoteTypes.SVT_NONE.key) || SrsBallotVoteTypes.SVT_NONE,
      SVT_RECONFIRM: groupedByVoteType.find(v => v.key === SrsBallotVoteTypes.SVT_RECONFIRM.key) || SrsBallotVoteTypes.SVT_RECONFIRM,
      SVT_REVISE: groupedByVoteType.find(v => v.key === SrsBallotVoteTypes.SVT_REVISE.key) || SrsBallotVoteTypes.SVT_REVISE,
      SVT_WITHDRAW: groupedByVoteType.find(v => v.key === SrsBallotVoteTypes.SVT_WITHDRAW.key) || SrsBallotVoteTypes.SVT_WITHDRAW,
    };

    return ballotResult;
  }

  getVoterEligibility(ballot: ISrsBallotDetail | null, ballotVote: ISrsBallotVote): string {
    const eligible = ballot && ballotVote && ballotVote.isEligible;

    const result = eligible ? appConstants.votingRights.eligible : appConstants.votingRights.notEligible;
    return result;
  }

}
